
import { createPDF, getPDFConfig } from './config';
import { 
  addHeader, 
  addReceiptDetails, 
  addDescription, 
  addCustomerDetails, 
  addBankDetails, 
  addBusinessDetails, 
  addFooter 
} from './contentGenerators';
import { ReceiptData } from './types';

/**
 * Generates a PDF receipt from receipt data
 */
export const generatePDFReceipt = async (receiptData: ReceiptData): Promise<string> => {
  console.log('=== PDF Generation Debug ===');
  console.log('Full receiptData:', JSON.stringify(receiptData, null, 2));
  console.log('Customer Name:', receiptData.customerName);
  console.log('Customer Email:', receiptData.customerEmail);
  console.log('Business Profile:', receiptData.businessProfile);
  console.log('Bank Details:', receiptData.bankDetails);
  console.log('Include Address:', receiptData.includeAddress);
  console.log('Customer Address:', receiptData.customerAddress);
  
  const pdf = createPDF();
  const config = getPDFConfig(pdf);

  // Pre-fetch the company logo if the user enabled it in payment settings.
  const rs: any =
    (receiptData as any).receiptSettings ||
    (receiptData.businessProfile as any)?.receipt_settings ||
    (receiptData.businessProfile as any)?.receiptSettings;
  const bp: any = receiptData.businessProfile || {};
  const logoSrc: string | undefined =
    bp.company_logo_url || bp.companyLogoUrl || bp.businessLogo || bp.logoUrl;
  if (rs?.includeLogo && logoSrc && !receiptData.logoDataUri) {
    try {
      if (logoSrc.startsWith('data:')) {
        receiptData.logoDataUri = logoSrc;
      } else {
        const res = await fetch(logoSrc);
        const blob = await res.blob();
        const buf = await blob.arrayBuffer();
        const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
        const mime = blob.type || 'image/png';
        receiptData.logoDataUri = `data:${mime};base64,${b64}`;
      }
    } catch (e) {
      console.warn('Could not fetch company logo for PDF:', e);
    }
  }

  let yPosition = 20;

  // Add all sections in order
  console.log('Adding header...');
  yPosition = addHeader(pdf, receiptData, config);

  
  console.log('Adding receipt details...');
  yPosition = addReceiptDetails(pdf, receiptData, config, yPosition);
  
  console.log('Adding description...');
  yPosition = addDescription(pdf, receiptData, config, yPosition);
  
  console.log('Adding customer details...');
  // If the totals block ended near the bottom, push the customer/bank
  // section onto a new page so they aren't clipped.
  if (yPosition > config.pageHeight - 70) {
    pdf.addPage();
    yPosition = config.margin + 5;
  }
  const customerStartY = yPosition;
  const customerEndY = addCustomerDetails(pdf, receiptData, config, yPosition);
  
  console.log('Adding bank details (right column)...');
  const bankEndY = addBankDetails(pdf, receiptData, config, customerStartY);
  
  yPosition = Math.max(customerEndY, bankEndY);
  
  console.log('Adding business details...');
  yPosition = addBusinessDetails(pdf, receiptData, config, yPosition);
  
  console.log('Adding footer...');
  addFooter(pdf, config, receiptData);
  
  console.log('=== PDF Generation Complete ===');
  
  // Return as base64 string
  return pdf.output('datauristring');
};

/**
 * Generate a Invoice PDF — header "INVOICE", includes bank
 * details so the customer knows where to pay.
 */
export const generateInvoicePDF = (
  data: Omit<ReceiptData, 'documentType'>
): Promise<string> => generatePDFReceipt({ ...data, documentType: 'invoice' });

/**
 * Generate a Receipt PDF — header "RECEIPT", no bank details (already paid).
 */
export const generateReceiptPDF = (
  data: Omit<ReceiptData, 'documentType'>
): Promise<string> => generatePDFReceipt({ ...data, documentType: 'receipt' });

/**
 * Generate an Estimate PDF — header "ESTIMATE", no bank details (not a request
 * for payment).
 */
export const generateEstimatePDF = (
  data: Omit<ReceiptData, 'documentType'>
): Promise<string> => generatePDFReceipt({ ...data, documentType: 'estimate' });
